import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { toast } from 'sonner';
import { DollarSign, Megaphone, Calendar as CalIcon, Award, Save } from 'lucide-react';
import { PARTNERSHIP_TYPES, fmtCurrency, ROLE_LABELS } from '../lib/utils';

const TYPES = [{ value: 'all', label: 'All Partnership Types (Overall)' }, ...PARTNERSHIP_TYPES];

export default function MemberGoalsPage() {
  const [period, setPeriod] = useState('monthly');
  const [users, setUsers] = useState([]);
  const [goals, setGoals] = useState([]); // all member_goals for selected period
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedType, setSelectedType] = useState('all');
  const [form, setForm] = useState({ revenue_goal: 0, outreach_goal: 0, meetings_goal: 0, closures_goal: 0 });
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/member-goals?period=${period}`).then((r) => setGoals(r.data));

  useEffect(() => {
    api.get('/users').then((r) => {
      const team = r.data.filter((u) => u.role !== 'viewer');
      setUsers(team);
      if (!selectedUser && team.length) setSelectedUser(team[0].id);
    });
  }, []);

  useEffect(() => { load(); }, [period]);

  useEffect(() => {
    if (!selectedUser) return;
    const g = goals.find((x) => x.user_id === selectedUser && x.partnership_type === selectedType && x.period === period);
    setForm({
      revenue_goal: g?.revenue_goal || 0,
      outreach_goal: g?.outreach_goal || 0,
      meetings_goal: g?.meetings_goal || 0,
      closures_goal: g?.closures_goal || 0,
    });
  }, [selectedUser, selectedType, goals, period]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/member-goals', {
        user_id: selectedUser,
        partnership_type: selectedType,
        period,
        revenue_goal: parseFloat(form.revenue_goal) || 0,
        outreach_goal: parseInt(form.outreach_goal) || 0,
        meetings_goal: parseInt(form.meetings_goal) || 0,
        closures_goal: parseInt(form.closures_goal) || 0,
      });
      toast.success('Goal saved');
      load();
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const memberGoalsFor = (uid) => goals.filter((g) => g.user_id === uid && g.period === period);

  return (
    <div className="space-y-6" data-testid="member-goals-page">
      <div className="bg-gradient-to-br from-navy to-navy/95 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="text-magenta text-[11px] font-semibold tracking-[0.12em] uppercase">Admin Settings</div>
            <h2 className="font-heading text-2xl font-bold mt-1">Per-Member KPI Goals</h2>
            <p className="text-white/70 text-sm mt-2 max-w-md">Set revenue, outreach, meeting, and closure goals per team member, optionally segmented by partnership type.</p>
          </div>
          <Tabs value={period} onValueChange={setPeriod}>
            <TabsList className="bg-white/10">
              <TabsTrigger value="monthly" data-testid="mg-monthly" className="data-[state=active]:bg-magenta data-[state=active]:text-white">Monthly</TabsTrigger>
              <TabsTrigger value="quarterly" data-testid="mg-quarterly" className="data-[state=active]:bg-magenta data-[state=active]:text-white">Quarterly</TabsTrigger>
              <TabsTrigger value="annual" data-testid="mg-annual" className="data-[state=active]:bg-magenta data-[state=active]:text-white">Annual</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
          <h3 className="font-heading text-sm font-bold text-navy mb-3 uppercase tracking-wider text-slate-500 text-[11px]">Team Members</h3>
          <div className="space-y-1">
            {users.map((u) => {
              const memberGoals = memberGoalsFor(u.id);
              const allGoal = memberGoals.find((g) => g.partnership_type === 'all');
              const initials = u.full_name.split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase();
              return (
                <button
                  key={u.id}
                  onClick={() => setSelectedUser(u.id)}
                  data-testid={`mg-user-${u.id}`}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition ${selectedUser === u.id ? 'bg-magenta/10 border border-magenta/30' : 'hover:bg-slate-50 border border-transparent'}`}
                >
                  <Avatar className="w-9 h-9">
                    <AvatarFallback className="bg-navy text-white text-xs font-semibold">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-navy truncate">{u.full_name}</div>
                    <div className="text-[11px] text-slate-500 uppercase tracking-wider">{ROLE_LABELS[u.role]}</div>
                  </div>
                  {allGoal?.revenue_goal > 0 && (
                    <span className="text-[10px] font-bold text-magenta">{fmtCurrency(allGoal.revenue_goal)}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-2 space-y-5">
          {selectedUser ? (
            <>
              <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500">Editing</div>
                    <h3 className="font-heading text-lg font-bold text-navy">
                      {users.find((u) => u.id === selectedUser)?.full_name}
                    </h3>
                  </div>
                  <Select value={selectedType} onValueChange={setSelectedType}>
                    <SelectTrigger className="w-64" data-testid="mg-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {[
                    { k: 'revenue_goal', l: 'Revenue Goal', i: DollarSign, prefix: '$' },
                    { k: 'outreach_goal', l: 'Outreach Goal', i: Megaphone },
                    { k: 'meetings_goal', l: 'Meetings Goal', i: CalIcon },
                    { k: 'closures_goal', l: 'Closures Goal', i: Award },
                  ].map((f) => (
                    <div key={f.k} className="bg-slate-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-magenta/10 text-magenta flex items-center justify-center">
                          <f.i className="w-4 h-4" />
                        </div>
                        <Label className="text-xs font-semibold text-slate-700">{f.l}</Label>
                      </div>
                      <div className="relative">
                        {f.prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{f.prefix}</span>}
                        <Input
                          type="number"
                          value={form[f.k]}
                          onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                          className={f.prefix ? 'pl-7' : ''}
                          data-testid={`mg-input-${f.k}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 flex justify-end">
                  <Button onClick={save} disabled={saving} className="bg-magenta hover:bg-magenta/90" data-testid="mg-save">
                    <Save className="w-4 h-4 mr-2" /> Save Goal
                  </Button>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
                <h4 className="font-heading text-sm font-bold text-navy mb-3 uppercase tracking-wider text-slate-500 text-[11px]">Goals set for this member</h4>
                {memberGoalsFor(selectedUser).length === 0 && (
                  <div className="text-sm text-slate-500 text-center py-4">No goals set yet</div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {memberGoalsFor(selectedUser).map((g) => (
                    <div key={`${g.user_id}-${g.partnership_type}`} className="border border-slate-200 rounded-lg p-3 flex items-center justify-between" data-testid={`mg-existing-${g.partnership_type}`}>
                      <div>
                        <div className="text-xs font-semibold text-navy capitalize">{g.partnership_type.replace('_', ' ')}</div>
                        <div className="text-[10px] text-slate-500">Rev {fmtCurrency(g.revenue_goal)} · {g.outreach_goal} outreach · {g.closures_goal} closures</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-2xl p-12 text-center text-slate-500">Select a team member to set goals</div>
          )}
        </div>
      </div>
    </div>
  );
}
